import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const stepBackgrounds = [
  "/images/read.png",
  "/images/receive.png",
  "/images/request.png",
  "/images/return.png",
];

const steps = [
  {
    number: "01",
    title: "REQUEST",
    bullets: [
      "Browse online catalogue",
      <>
        Select and queue books in{" "}
        <Link
          to="/wishlist"
          className="text-primary font-semibold hover:text-primary-dark transition"
        >
          My Wishlist
        </Link>
      </>,
      "Request book delivery",
    ],
  },
  {
    number: "02",
    title: "RECEIVE",
    bullets: [
      "Priority delivery in 1‑3 days",
      "Free doorstep delivery",
      "Track your delivery in real-time",
    ],
  },
  {
    number: "03",
    title: "READ",
    bullets: [
      "Enjoy unlimited reading time",
      "No due dates or late fees",
      "Notify when finished",
    ],
  },
  {
    number: "04",
    title: "RETURN",
    bullets: [
      "Free pickup & next set delivery",
      "Easy return scheduling via your account",
      "Repeat the process",
    ],
  },
];

const HowItWorks = () => (
  <section className="py-1 bg-gradient-to-b from-primary.shade1 to-white font-nunito relative">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-16 text-center space-y-6"
      >
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-800">
          How It Works
        </h2>
        <div className="flex justify-center items-center space-x-2">
          <div className="w-20 h-2 bg-primary rounded-full animate-pulse" />
          <div className="w-10 h-2 bg-primary.shade3 rounded-full" />
          <div className="w-6 h-2 bg-primary.shade2 rounded-full" />
        </div>
      </motion.div>

      {/* Step Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-10">
      {steps.map(({ number, title, bullets }, index) => {
  const cardContent = (
    <motion.div
    key={number}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
    viewport={{ once: true }}
    className="group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 bg-white flex flex-col min-h-[520px]"
  >
            {/* Image */}
            <div
              className="relative h-64 w-full bg-center bg-cover"
              style={{ backgroundImage: `url(${stepBackgrounds[index]})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent rounded-t-3xl" />
            </div>

            {/* Content */}
            <div className="p-6 pt-2 space-y-6 relative z-10 bg-white">
              {/* Badge */}
              <div className="absolute -top-8 right-6 z-20">
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary.shade4 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-xl border-4 border-white">
                  {number}
                </div>
              </div>

              {/* Title with underline */}
              <div className="text-center space-y-1">
                {/* Title */}
                <h3 className="text-xl font-bold text-gray-800 group-hover:text-primary transition-colors">
                  {title}
                </h3>

                {/* Underline */}
                <div className="flex justify-center items-center">
                  <div className="shrink-0 w-16 h-1 bg-primary rounded-full animate-pulse" />
                </div>
              </div>

              {/* Bullets */}
              <ul className="space-y-2">
                {bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle className="w-5 h-5 text-primary.shade3 mr-3" />
                    <span className="text-base">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
            );

            return (
              <div key={number}>
                {index === 0 ? (
                  <Link to="/explore-books">{cardContent}</Link>
                ) : index === 3 ? (
                  <Link to="/myOrders">{cardContent}</Link>
                ) : (
                  cardContent
                )}
              </div>
            );
          })}
      </div>
    </div>
  </section>
);

export default HowItWorks;
